/**
 * 組織マスタストア
 * 組織・部署・チーム・メンバーの階層管理
 */

import { create } from 'zustand'
import type {
  Organization,
  OrganizationDepartment,
  OrganizationTeam,
  OrganizationMember,
  OrganizationTreeNode,
} from '@/lib/types/organization'
import { UNSET_ROLE_CODE } from '@/lib/constants/roles'

// 型を再エクスポート
export type { OrganizationMember, OrganizationDepartment, OrganizationTeam, OrganizationTreeNode }

const DEFAULT_ORG: Organization = {
  id: 'org-ghouse',
  name: 'Gハウス',
  code: 'ghouse',
  isActive: true,
  createdAt: '',
  updatedAt: '',
}

// ────────────────────────────────────────
// ストア定義
// ────────────────────────────────────────
interface OrganizationStore {
  organization: Organization
  departments: OrganizationDepartment[]
  teams: OrganizationTeam[]
  members: OrganizationMember[]

  // DBロード状態
  _dbLoaded: boolean
  _dbLoadError: string | null
  _dbLoadErrorCode: string | null // 断られた応答の code（2段階認証の登録へ誘導中を見分ける・AUTH-01 補足7）
  _dbLoadedAt: string | null

  // DBロード
  loadFromDB: () => Promise<void>

  // codeで部署検索（Phase 2用ヘルパー）
  getDepartmentByCode: (code: string) => OrganizationDepartment | undefined

  // 組織アクション
  updateOrganization: (updates: Partial<Organization>) => void

  // 部署アクション
  addDepartment: (
    dept: Omit<OrganizationDepartment, 'id' | 'createdAt' | 'updatedAt'>
  ) => OrganizationDepartment
  updateDepartment: (id: string, updates: Partial<OrganizationDepartment>) => void
  deleteDepartment: (id: string) => void

  // チームアクション
  addTeam: (team: Omit<OrganizationTeam, 'id' | 'createdAt' | 'updatedAt'>) => OrganizationTeam
  updateTeam: (id: string, updates: Partial<OrganizationTeam>) => void
  deleteTeam: (id: string) => void

  // メンバーアクション
  addMember: (
    member: Omit<OrganizationMember, 'id' | 'createdAt' | 'updatedAt'>
  ) => OrganizationMember
  updateMember: (id: string, updates: Partial<OrganizationMember>) => void
  deleteMember: (id: string) => void

  // ヘルパー関数
  getDepartmentById: (id: string) => OrganizationDepartment | undefined
  getTeamById: (id: string) => OrganizationTeam | undefined
  getMemberById: (id: string) => OrganizationMember | undefined
  getTeamsByDepartment: (departmentId: string) => OrganizationTeam[]
  getMembersByTeam: (teamId: string) => OrganizationMember[]
  getMembersByDepartment: (departmentId: string) => OrganizationMember[]
  getChildDepartments: (parentId: string | null) => OrganizationDepartment[]

  // ツリー構造取得
  getOrganizationTree: () => OrganizationTreeNode

  // kintone連携
  findMemberByKintoneId: (kintoneUserId: string) => OrganizationMember | undefined
  syncMemberWithKintone: (memberId: string, kintoneUserId: string) => void
}

export const useOrganizationStore = create<OrganizationStore>()(
  // persist(
  (set, get) => ({
    organization: DEFAULT_ORG,
    departments: [],
    teams: [],
    members: [],

    // DBロード状態
    _dbLoaded: false,
    _dbLoadError: null,
    _dbLoadErrorCode: null,
    _dbLoadedAt: null,

    // DBからデータをロード。
    // ユーザー管理画面の「退職日」列表示・末尾配置のため includeRetired=true で取得する
    // （組織図など既存画面も members には退職者が混入するが、isActive=false なので既存フィルタで除外される）
    loadFromDB: async () => {
      let errorCode: string | null = null
      try {
        const res = await fetch('/api/organization/summary?includeRetired=true')

        // 401 はサーバー側セッション切れ。zustand persist の isAuthenticated=true が
        // localStorage 由来でゴースト復元されたケース。toast を出さず silent skip し、
        // ミドルウェア/AuthProvider に再認証を委ねる。
        if (res.status === 401) {
          set({ _dbLoaded: false, _dbLoadError: null, _dbLoadErrorCode: null })
          return
        }

        if (!res.ok) {
          // 本文が読めないときは code なし＝今までどおりの文（断られた理由を見分けるだけ・AUTH-01 補足7）
          const body = (await res.json().catch(() => ({ code: null }))) as { code?: unknown }
          errorCode = typeof body.code === 'string' ? body.code : null
          throw new Error(`API error: summary=${res.status}`)
        }

        const json = await res.json()
        const summary = json.data as {
          departments: Record<string, unknown>[]
          teams: Record<string, unknown>[]
          members: Record<string, unknown>[]
        }

        const dbDepts = summary.departments
        const dbTeams = summary.teams
        const dbMembers = summary.members

        // 安全チェック: 部署・メンバーが1件以上存在することを確認（チームは任意）
        if (!dbDepts?.length || !dbMembers?.length) {
          set({
            _dbLoaded: false,
            _dbLoadErrorCode: null,
            _dbLoadError: `データ不足でスキップ: 部署=${dbDepts?.length ?? 0} メンバー=${dbMembers?.length ?? 0}`,
          })
          return
        }

        // DB型 → Zustand型に変換
        const toStr = (v: unknown): string =>
          v instanceof Date ? v.toISOString() : String(v ?? '')

        // 監査 ADM1-M13: /api/organization/summary は departmentAdapter/teamAdapter/
        // personAdapter（drizzle）→ apiSuccess でそのまま JSON 化されるため camelCase のみを返す
        // （lib/api/auth.ts の apiSuccess はキー名変換をしない）。snake_case 対応分岐は死んだ
        // コードだったため撤去し camelCase のみを読む。
        const departments: OrganizationDepartment[] = dbDepts.map((d) => {
          const cdt = d.chartDisplayType as 'column' | 'group' | 'hidden' | undefined
          return {
            id: String(d.id),
            organizationId: String(d.tenantId ?? 'org-ghouse'),
            name: String(d.name),
            code: String(d.code),
            parentId: d.parentId ? String(d.parentId) : null,
            managerId: d.managerId ? String(d.managerId) : null,
            sortOrder: Number(d.sortOrder ?? 0),
            numericCode: d.numericCode != null ? String(d.numericCode) : null,
            isActive: Boolean(d.isActive ?? true),
            category: (d.category ?? null) as string | null,
            showInOrgchart: Boolean(d.showInOrgchart ?? true),
            chartDisplayType: cdt ?? 'column',
            createdAt: toStr(d.createdAt),
            updatedAt: toStr(d.updatedAt),
          }
        })

        const teams: OrganizationTeam[] = dbTeams.map((t) => ({
          id: String(t.id),
          departmentId: String(t.departmentId),
          name: String(t.name),
          code: String(t.code),
          leaderId: t.leaderId ? String(t.leaderId) : null,
          sortOrder: Number(t.sortOrder ?? 0),
          isActive: Boolean(t.isActive ?? true),
          createdAt: toStr(t.createdAt),
          updatedAt: toStr(t.updatedAt),
        }))

        const members: OrganizationMember[] = dbMembers.map((m) => ({
          id: String(m.id),
          userId: m.userId ? String(m.userId) : null,
          teamId: m.teamId ? String(m.teamId) : null,
          departmentId: String(m.departmentId),
          name: String(m.name),
          // 姓名・フリガナ（正本カラム）。API(mapPersonRowToMember)は供給するが従来マッパーが
          // 落としており、/admin/users の編集ダイアログでフリガナが空欄化 → 保存時に null で
          // DB のフリガナを上書き消去するデータ消失バグがあった。ここで補填して防ぐ。
          lastName: m.lastName != null ? String(m.lastName) : null,
          firstName: m.firstName != null ? String(m.firstName) : null,
          lastNameKana: m.lastNameKana != null ? String(m.lastNameKana) : null,
          firstNameKana: m.firstNameKana != null ? String(m.firstNameKana) : null,
          employeeCode: m.employeeCode != null ? String(m.employeeCode) : null,
          email: String(m.email ?? ''),
          phone: m.phone ? String(m.phone) : undefined,
          role: String(m.role ?? UNSET_ROLE_CODE),
          kintoneUserId: m.kintoneUserId ? String(m.kintoneUserId) : undefined,
          kintoneUserName: m.kintoneUserName ? String(m.kintoneUserName) : undefined,
          googleChatId: m.googleChatId ? String(m.googleChatId) : undefined,
          lineWorksUrl: m.lineWorksUrl ? String(m.lineWorksUrl) : undefined,
          onLeave: m.onLeave ? Boolean(m.onLeave) : undefined,
          // 雇用形態・入社前フラグ（persons.status 由来・API の MemberEnriched が供給）。
          // 従業員数の統一カウント(countTotalUsers)が external/pending を判定するのに必須。
          // これを落とすと /admin/users 側で pending が除外されず /admin/organization と件数がズレる。
          employmentType: m.employmentType != null ? String(m.employmentType) : null,
          isPending: Boolean(m.isPending ?? false),
          sortOrder: m.sortOrder ? Number(m.sortOrder) : undefined,
          isActive: Boolean(m.isActive ?? true),
          numericRoleCode: m.numericRoleCode != null ? Number(m.numericRoleCode) : null,
          // 組織図の役職表示は name 直接・並び順は level（member_role_masters）。code 採番に非依存
          roleMasterName: m.roleMasterName != null ? String(m.roleMasterName) : null,
          roleMasterLevel: m.roleMasterLevel != null ? Number(m.roleMasterLevel) : null,
          isConcurrent: Boolean(m.isConcurrent ?? false),
          createdAt: toStr(m.createdAt),
          updatedAt: toStr(m.updatedAt),
          // 2026-06-19: ユーザー管理画面の「入社日」「退職日」列表示用
          hireDate: m.hireDate as string | Date | null | undefined,
          retiredAt: m.retiredAt as string | Date | null | undefined,
        }))

        set({
          departments,
          teams,
          members,
          _dbLoaded: true,
          _dbLoadError: null,
          _dbLoadErrorCode: null,
          _dbLoadedAt: new Date().toISOString(),
        })
      } catch (error) {
        set({
          _dbLoadError: error instanceof Error ? error.message : String(error),
          _dbLoadErrorCode: errorCode,
        })
      }
    },

    // codeで部署検索
    getDepartmentByCode: (code) => get().departments.find((d) => d.code === code),

    // 組織アクション
    updateOrganization: (updates) => {
      set((state) => ({
        organization: {
          ...state.organization,
          ...updates,
          updatedAt: new Date().toISOString(),
        },
      }))
    },

    // 部署アクション
    addDepartment: (deptInput) => {
      const now = new Date().toISOString()
      const newDept: OrganizationDepartment = {
        ...deptInput,
        id: `dept-${Date.now()}`,
        createdAt: now,
        updatedAt: now,
      }
      set((state) => ({ departments: [...state.departments, newDept] }))
      return newDept
    },

    updateDepartment: (id, updates) => {
      set((state) => ({
        departments: state.departments.map((d) =>
          d.id === id ? { ...d, ...updates, updatedAt: new Date().toISOString() } : d
        ),
      }))
    },

    deleteDepartment: (id) => {
      set((state) => ({
        // 削除対象を除外し、子部署の親をnullに更新
        departments: state.departments
          .filter((d) => d.id !== id)
          .map((d) => (d.parentId === id ? { ...d, parentId: null } : d)),
      }))
    },

    // チームアクション
    addTeam: (teamInput) => {
      const now = new Date().toISOString()
      const newTeam: OrganizationTeam = {
        ...teamInput,
        id: `team-${Date.now()}`,
        createdAt: now,
        updatedAt: now,
      }
      set((state) => ({ teams: [...state.teams, newTeam] }))
      return newTeam
    },

    updateTeam: (id, updates) => {
      set((state) => ({
        teams: state.teams.map((t) =>
          t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
        ),
      }))
    },

    deleteTeam: (id) => {
      set((state) => ({
        teams: state.teams.filter((t) => t.id !== id),
        // メンバーのチームをnullに
        members: state.members.map((m) => (m.teamId === id ? { ...m, teamId: null } : m)),
      }))
    },

    // メンバーアクション
    addMember: (memberInput) => {
      const now = new Date().toISOString()
      const newMember: OrganizationMember = {
        ...memberInput,
        id: `member-${Date.now()}`,
        createdAt: now,
        updatedAt: now,
      }
      set((state) => ({ members: [...state.members, newMember] }))
      return newMember
    },

    updateMember: (id, updates) => {
      set((state) => ({
        members: state.members.map((m) =>
          m.id === id ? { ...m, ...updates, updatedAt: new Date().toISOString() } : m
        ),
      }))
    },

    deleteMember: (id) => {
      set((state) => ({
        members: state.members.filter((m) => m.id !== id),
      }))
    },

    // ヘルパー関数
    getDepartmentById: (id) => get().departments.find((d) => d.id === id),
    getTeamById: (id) => get().teams.find((t) => t.id === id),
    getMemberById: (id) => get().members.find((m) => m.id === id),

    getTeamsByDepartment: (departmentId) =>
      get()
        .teams.filter((t) => t.departmentId === departmentId)
        .sort((a, b) => a.sortOrder - b.sortOrder),

    getMembersByTeam: (teamId) => get().members.filter((m) => m.teamId === teamId && m.isActive),

    getMembersByDepartment: (departmentId) =>
      get().members.filter((m) => m.departmentId === departmentId && m.isActive),

    getChildDepartments: (parentId) =>
      get()
        .departments.filter((d) => d.parentId === parentId && d.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder),

    // ツリー構造取得
    getOrganizationTree: () => {
      const { organization, departments, teams, members } = get()

      const buildDepartmentNode = (dept: OrganizationDepartment): OrganizationTreeNode => {
        const childDepts = departments
          .filter((d) => d.parentId === dept.id && d.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)

        const deptTeams = teams
          .filter((t) => t.departmentId === dept.id && t.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)

        const deptMembers = members.filter(
          (m) => m.departmentId === dept.id && !m.teamId && m.isActive
        )

        const children: OrganizationTreeNode[] = [
          // 子部署
          ...childDepts.map(buildDepartmentNode),
          // チーム
          ...deptTeams.map((team): OrganizationTreeNode => ({
            id: team.id,
            type: 'team',
            name: team.name,
            code: team.code,
            children: members
              .filter((m) => m.teamId === team.id && m.isActive)
              .map((m): OrganizationTreeNode => ({
                id: m.id,
                type: 'member',
                name: m.name,
                data: m,
              })),
            data: team,
          })),
          // チームに属さないメンバー
          ...deptMembers.map((m): OrganizationTreeNode => ({
            id: m.id,
            type: 'member',
            name: m.name,
            data: m,
          })),
        ]

        return {
          id: dept.id,
          type: 'department',
          name: dept.name,
          code: dept.code,
          children,
          data: dept,
        }
      }

      const rootDepartments = departments
        .filter((d) => !d.parentId && d.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder)

      return {
        id: organization.id,
        type: 'organization',
        name: organization.name,
        code: organization.code,
        children: rootDepartments.map(buildDepartmentNode),
        data: organization,
      }
    },

    // kintone連携
    findMemberByKintoneId: (kintoneUserId) =>
      get().members.find((m) => m.kintoneUserId === kintoneUserId),

    syncMemberWithKintone: (memberId, kintoneUserId) => {
      set((state) => ({
        members: state.members.map((m) =>
          m.id === memberId ? { ...m, kintoneUserId, updatedAt: new Date().toISOString() } : m
        ),
      }))
    },
  })
  // )
)
